import { useState } from 'react';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Checkbox } from '@/components/ui/checkbox';
import { format, addMonths } from 'date-fns';
import type { RecurrenceRule } from '@/lib/recurrenceUtils';

interface RecurrenceSelectorProps {
  value?: RecurrenceRule | null;
  onChange: (rule: RecurrenceRule | null) => void;
  startDate: Date;
}

export function RecurrenceSelector({ value, onChange, startDate }: RecurrenceSelectorProps) {
  const [isRecurring, setIsRecurring] = useState(!!value);
  const [frequency, setFrequency] = useState<RecurrenceRule['frequency']>(value?.frequency || 'weekly');
  const [interval, setInterval] = useState(value?.interval || 1);
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>(value?.daysOfWeek || []);
  const [endType, setEndType] = useState<'never' | 'date' | 'count'>(
    value?.endDate ? 'date' : value?.count ? 'count' : 'never'
  );
  const [endDate, setEndDate] = useState(value?.endDate || format(addMonths(new Date(), 6), 'yyyy-MM-dd'));
  const [count, setCount] = useState(value?.count || 10);

  const weekDays = [
    { value: 0, label: 'Sun' },
    { value: 1, label: 'Mon' },
    { value: 2, label: 'Tue' },
    { value: 3, label: 'Wed' },
    { value: 4, label: 'Thu' },
    { value: 5, label: 'Fri' },
    { value: 6, label: 'Sat' },
  ];

  const handleToggle = (enabled: boolean) => {
    setIsRecurring(enabled);
    if (!enabled) {
      onChange(null);
    } else {
      updateRule();
    }
  };

  const updateRule = () => {
    if (!isRecurring) return;

    const rule: RecurrenceRule = {
      frequency,
      interval,
      ...(frequency === 'weekly' && daysOfWeek.length > 0 && { daysOfWeek }),
      ...(endType === 'date' && { endDate }),
      ...(endType === 'count' && { count }),
    };

    onChange(rule);
  };

  const handleFrequencyChange = (newFrequency: RecurrenceRule['frequency']) => {
    setFrequency(newFrequency);
    setTimeout(updateRule, 0);
  };

  const handleIntervalChange = (value: string) => {
    const num = parseInt(value) || 1;
    setInterval(num);
    setTimeout(updateRule, 0);
  };

  const handleDayToggle = (day: number, checked: boolean) => {
    const newDays = checked
      ? [...daysOfWeek, day].sort()
      : daysOfWeek.filter(d => d !== day);
    setDaysOfWeek(newDays);
    setTimeout(updateRule, 0);
  };

  const handleEndTypeChange = (type: 'never' | 'date' | 'count') => {
    setEndType(type);
    setTimeout(updateRule, 0);
  };

  return (
    <div className="space-y-4 p-4 border rounded-lg">
      <div className="flex items-center justify-between">
        <Label htmlFor="recurring-toggle">Repeat Event</Label>
        <Switch
          id="recurring-toggle"
          checked={isRecurring}
          onCheckedChange={handleToggle}
        />
      </div>

      {isRecurring && (
        <>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Repeat every</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="number"
                  min="1"
                  value={interval}
                  onChange={(e) => handleIntervalChange(e.target.value)}
                  className="w-20"
                />
                <Select value={frequency} onValueChange={handleFrequencyChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="daily">Day(s)</SelectItem>
                    <SelectItem value="weekly">Week(s)</SelectItem>
                    <SelectItem value="monthly">Month(s)</SelectItem>
                    <SelectItem value="yearly">Year(s)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {frequency === 'weekly' && (
            <div>
              <Label>Repeat on</Label>
              <div className="flex gap-2 mt-2">
                {weekDays.map(day => (
                  <div key={day.value} className="flex items-center">
                    <Checkbox
                      id={`day-${day.value}`}
                      checked={daysOfWeek.includes(day.value)}
                      onCheckedChange={(checked) => handleDayToggle(day.value, checked as boolean)}
                    />
                    <label htmlFor={`day-${day.value}`} className="ml-1 text-sm cursor-pointer">
                      {day.label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>Ends</Label>
            <RadioGroup value={endType} onValueChange={handleEndTypeChange} className="mt-2 space-y-2">
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="never" id="end-never" />
                <Label htmlFor="end-never" className="font-normal cursor-pointer">Never</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="date" id="end-date" />
                <Label htmlFor="end-date" className="font-normal cursor-pointer">On</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => {
                    setEndDate(e.target.value);
                    setEndType('date');
                    setTimeout(updateRule, 0);
                  }}
                  disabled={endType !== 'date'}
                  className="w-40"
                />
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="count" id="end-count" />
                <Label htmlFor="end-count" className="font-normal cursor-pointer">After</Label>
                <Input
                  type="number"
                  min="1"
                  value={count}
                  onChange={(e) => {
                    setCount(parseInt(e.target.value) || 1);
                    setEndType('count');
                    setTimeout(updateRule, 0);
                  }}
                  disabled={endType !== 'count'}
                  className="w-20"
                />
                <span className="text-sm text-muted-foreground">occurrences</span>
              </div>
            </RadioGroup>
          </div>
        </>
      )}
    </div>
  );
}
